"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { RouteLoadingLinkStatus } from "@/features/navigation/ui/components/RouteLoadingSpinner";
import { ProductsSlider } from "@/features/products/ui";
import type { ProductForClient } from "@/lib/shared/types/interfaces";
import { Section } from "@/ui";

type HomeLabelKey = "new" | "weekly" | "sales";

const SECTION_LABEL_KEYS: Record<HomeLabelKey, "new_label" | "weekly_label" | "sales_label"> = {
  new: "new_label",
  weekly: "weekly_label",
  sales: "sales_label",
};

interface HomeLabeledProductsSectionProps {
  products: ProductForClient[];
  sectionKey: HomeLabelKey;
  imageRatio?: number;
  "data-testid": string;
}

export const HomeLabeledProductsSection = (props: HomeLabeledProductsSectionProps) => {
  const { products, sectionKey, imageRatio, "data-testid": dataTestId } = props;
  const homeT = useTranslations("home");
  const generalT = useTranslations("general");
  const locale = useLocale();

  if (products.length === 0) {
    return null;
  }

  return (
    <Section
      data-testid={dataTestId}
      className="section-spacing"
    >
      {/* Section Header - luxury styling */}
      <div className="mb-10 flex items-end justify-between gap-4">
        <div>
          <span className="section-label block mb-3">
            {homeT(SECTION_LABEL_KEYS[sectionKey])}
          </span>
          <h2 className="heading-display">{homeT(sectionKey)}</h2>
        </div>
        <Link
          href={`/${locale}/products/categories/${sectionKey}`}
          className="shrink-0 text-sm font-medium text-muted-foreground underline-offset-4 transition-all hover:text-foreground hover:underline hidden sm:block"
        >
          {generalT("view_all")}
          <RouteLoadingLinkStatus />
        </Link>
      </div>
      <ProductsSlider
        arrowVariant="top-overlay"
        ariaLabel={homeT(sectionKey)}
        imageRatio={imageRatio}
        products={products}
      />
      {/* Mobile view all link */}
      <div className="mt-6 text-center sm:hidden">
        <Link
          href={`/${locale}/products/categories/${sectionKey}`}
          className="text-sm font-medium text-muted-foreground underline-offset-4 transition-all hover:text-foreground hover:underline"
        >
          {generalT("view_all")}
          <RouteLoadingLinkStatus />
        </Link>
      </div>
    </Section>
  );
};
